
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AutocompleteSelect from "@/components/ui/autocomplete-select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { DatePicker } from "@/components/ui/date-picker";
import { GuestManager, Guest } from "@/components/ui/guest-manager";
import { MapPin, Users, Send, Euro, Heart, Utensils, Camera, Mountain, Palette, MessageSquare, Globe, Waves, TreePine, PawPrint, ShoppingBag, Calendar, Moon, Sparkles, Trophy, Baby, Umbrella } from "lucide-react";
import { toast } from "@/hooks/useToast";
import Loader from "@/components/ui/loader";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Json } from "@/integrations/supabase/types";
import { getRandomSample } from "@/fixtures/sampleTripRequest";
import { showDevError } from "@/components/ui/dev-error-toast";
import { useNavigate } from "react-router-dom";
import { Mail, ArrowLeft } from "lucide-react";

// Types

interface ContactInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface TravelDates {
  startDate: string;
  endDate: string;
  flexible: boolean;
}

interface Budget {
  min: string;
  max: string;
  flexible: boolean;
}

interface FormData {
  destinations: string[];
  departurePoint: string;
  needsTransportation: boolean;
  autoAssignDesigner: boolean; // Nuovo flag per gestire la checkbox
  travelDesigner: string | null; // UUID del designer o null
  totalGuests: number;
  guests: Guest[];
  travelDates: TravelDates;
  budget: Budget;
  interests: string[];
  additionalNotes: string;
  contactInfo: ContactInfo;
}

// Constants
const interestOptions = [
  { id: "beach", label: "Beach", icon: Waves },
  { id: "mountain", label: "Mountain", icon: Mountain },
  { id: "wildlife", label: "Wildlife", icon: TreePine },
  { id: "relax", label: "Relax", icon: Umbrella },
  { id: "spa", label: "Spa", icon: Sparkles },
  { id: "shopping", label: "Shopping", icon: ShoppingBag },
  { id: "food", label: "Food", icon: Utensils },
  { id: "culture", label: "Culture", icon: Palette },
  { id: "events", label: "Events", icon: Calendar },
  { id: "nightlife", label: "Nightlife", icon: Moon },
  { id: "romantic", label: "Romantic Getaways", icon: Heart },
  { id: "sports", label: "Sports", icon: Trophy },
  { id: "family", label: "Family", icon: Baby },
  { id: "pet-friendly", label: "Pet-Friendly Places", icon: PawPrint },
  { id: "photography", label: "Photography Spots", icon: Camera }
];



// Helper Components
const IconInput = ({
  icon: Icon,
  placeholder,
  value,
  onChange,
  type = "text",
  className = "",
  hasError = false
}: {
  icon: React.ComponentType<{ className?: string }>;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  className?: string;
  hasError?: boolean;
}) => (
  <div className="relative">
    <Icon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
    <Input
      type={type}
      placeholder={placeholder}
      className={`pl-12 pr-4 py-3 rounded-xl ${className}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      hasError={hasError}
    />
  </div>
);



const RequestTrip = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const destinationInputRef = useRef<HTMLInputElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());

  // Stato per i travel designer caricati da Supabase
  const [travelDesigners, setTravelDesigners] = useState<Array<{
    id: string;
    name: string;
    specialty: string;
    avatar: string;
  }>>([]);

  // Carica i travel designer da Supabase
  const loadTravelDesigners = async () => {
    try {
      // Query semplificata: ora il ruolo è direttamente nella tabella users
      const { data, error } = await supabase
        .from('users')
        .select('id, first_name, last_name, specialization, approval_status')
        .eq('role', 'travel_designer')
        .eq('approval_status', 'approved');

      if (error) {
        console.error('Error loading travel designers:', error);
        showDevError({ error, context: 'travel designers loading' });
        return;
      }

      if (data) {
        const designers = data.map(user => ({
          id: user.id,
          name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Designer anonimo',
          specialty: user.specialization || 'Travel Specialist',
          avatar: '👨‍💼'
        }));
        setTravelDesigners(designers);
      }
    } catch (error) {
      console.error('Error loading travel designers:', error);
      showDevError({ error, context: 'travel designers loading' });
    }
  };

  // Carica i travel designer al mount del componente
  useEffect(() => {
    loadTravelDesigners();
  }, []);

  const [formData, setFormData] = useState<FormData>({
    destinations: [],
    departurePoint: "",
    needsTransportation: false,
    autoAssignDesigner: true, // Di default lascia scegliere a Place2Be
    travelDesigner: null,
    totalGuests: 1,
    guests: [{ id: 1, age: 25 }],
    travelDates: {
      startDate: "",
      endDate: "",
      flexible: false
    },
    budget: {
      min: "",
      max: "",
      flexible: false
    },
    interests: [],
    additionalNotes: "",
    contactInfo: {
      firstName: "",
      lastName: "",
      email: "",
      phone: ""
    }
  });

  // Helper functions
  const updateFormData = (updates: Partial<FormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
    
    // Clear validation errors for updated fields
    const newErrors = new Set(validationErrors);
    Object.keys(updates).forEach(key => {
      if (key === 'destinations' && updates.destinations && updates.destinations.length > 0) {
        newErrors.delete('destinations');
      } else if (key === 'travelDesigner' && updates.travelDesigner) {
        newErrors.delete('travelDesigner');
      } else if (key === 'autoAssignDesigner') {
        // Se auto-assign è true, rimuovi l'errore di validazione del designer
        if (updates.autoAssignDesigner) {
          newErrors.delete('travelDesigner');
        }
      } else if (key === 'departurePoint' && updates.departurePoint) {
        newErrors.delete('departurePoint');
      }
    });
    setValidationErrors(newErrors);
  };

  const updateNestedField = <K extends keyof FormData>(
    field: K,
    updates: Partial<FormData[K]>
  ) => {
    setFormData(prev => {
      const currentValue = prev[field];
      if (typeof currentValue === 'object' && currentValue !== null) {
        return {
          ...prev,
          [field]: { ...currentValue, ...updates }
        };
      }
      return prev;
    });
    
    // Clear validation errors for updated nested fields
    const newErrors = new Set(validationErrors);
    if (field === 'travelDates') {
      if ('startDate' in updates && updates.startDate) newErrors.delete('travelStartDate');
      if ('endDate' in updates && updates.endDate) newErrors.delete('travelEndDate');
    } else if (field === 'budget') {
      if ('min' in updates && updates.min) newErrors.delete('budgetMin');
      if ('max' in updates && updates.max) newErrors.delete('budgetMax');
    } else if (field === 'contactInfo') {
      if ('firstName' in updates && updates.firstName) newErrors.delete('contactFirstName');
      if ('lastName' in updates && updates.lastName) newErrors.delete('contactLastName');
      if ('email' in updates && updates.email) newErrors.delete('contactEmail');
      if ('phone' in updates && updates.phone) newErrors.delete('contactPhone');
    }
    setValidationErrors(newErrors);
  };

  // Destination functions
  const addDestination = (destination: string) => {
    if (destination && !formData.destinations.includes(destination)) {
      updateFormData({
        destinations: [...formData.destinations, destination]
      });
      // Clear destinations validation error
      const newErrors = new Set(validationErrors);
      newErrors.delete('destinations');
      setValidationErrors(newErrors);
    }
  };

  const removeDestination = (destination: string) => {
    updateFormData({
      destinations: formData.destinations.filter(d => d !== destination)
    });
  };

  const handleAddDestination = () => {
    if (destinationInputRef.current) {
      const value = destinationInputRef.current.value.trim();
      if (value) {
        addDestination(value);
        destinationInputRef.current.value = '';
      }
    }
  };

  // Guest functions
  const addGuest = () => {
    const newGuest = { id: Date.now(), age: 25 };
    updateFormData({
      totalGuests: formData.totalGuests + 1,
      guests: [...formData.guests, newGuest]
    });
  };

  const removeGuest = () => {
    if (formData.guests.length <= 1) return;

    updateFormData({
      totalGuests: formData.totalGuests - 1,
      guests: formData.guests.slice(0, -1)
    });
  };

  const updateGuestAge = (guestId: number, newAge: number) => {
    // Ensure age is always at least 1
    const validAge = Math.max(1, newAge);
    updateFormData({
      guests: formData.guests.map(guest =>
        guest.id === guestId ? { ...guest, age: validAge } : guest
      )
    });
  };

  // Interest functions
  const toggleInterest = (interest: string) => {
    updateFormData({
      interests: formData.interests.includes(interest)
        ? formData.interests.filter(i => i !== interest)
        : [...formData.interests, interest]
    });
  };

  // Funzione per applicare dati di esempio (solo in development)
  const applySampleData = () => {
    const sampleData = getRandomSample();
    setFormData(sampleData);
    toast({
      title: "Dati di esempio applicati! 🎉",
      description: "Il form è stato compilato con dati di esempio per il testing.",
    });
  };

  // Validation function
  const validateForm = () => {
    const errors = new Set<string>();
    
    // Configuration array for validation rules
    const validationRules = [
      {
        key: 'destinations',
        condition: () => formData.destinations.length === 0
      },
      {
        key: 'travelDesigner',
        condition: () => !formData.autoAssignDesigner && !formData.travelDesigner
      },
      {
        key: 'departurePoint',
        condition: () => !formData.departurePoint
      },
      {
        key: 'travelStartDate',
        condition: () => !formData.travelDates.startDate
      },
      {
        key: 'travelEndDate',
        condition: () => !formData.travelDates.endDate
      },
      {
        key: 'budgetMin',
        condition: () => !formData.budget.min
      },
      {
        key: 'budgetMax',
        condition: () => !formData.budget.max
      },
      {
        key: 'contactFirstName',
        condition: () => !formData.contactInfo.firstName
      },
      {
        key: 'contactLastName',
        condition: () => !formData.contactInfo.lastName
      },
      {
        key: 'contactEmail',
        condition: () => !formData.contactInfo.email
      },
      {
        key: 'contactPhone',
        condition: () => !formData.contactInfo.phone
      }
    ];
    
    // Apply validation rules
    validationRules.forEach(rule => {
      if (rule.condition()) {
        errors.add(rule.key);
      }
    });
    
    setValidationErrors(errors);
    return errors.size === 0;
  };

  // Submit function
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Clear previous validation errors
      setValidationErrors(new Set());
      
      // Validate form
      if (!validateForm()) {
        toast({
          title: "Campi richiesti mancanti",
          description: "Compila tutti i campi obbligatori per procedere.",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      const { data, error } = await supabase
        .from('travel_requests')
        .insert({
          user_id: user?.id || null, // Ora può essere null per richieste anonime
          destinations: formData.destinations,
          departure_point: formData.departurePoint,
          needs_transportation: formData.needsTransportation,
          total_guests: formData.totalGuests,
          guests_data: formData.guests as unknown as Json, // Convert to JSON format
          travel_dates_flexible: formData.travelDates.flexible,
          travel_start_date: formData.travelDates.startDate,
          travel_end_date: formData.travelDates.endDate,
          budget_min: parseFloat(formData.budget.min),
          budget_max: parseFloat(formData.budget.max),
          budget_flexible: formData.budget.flexible,
          interests: formData.interests.length > 0 ? formData.interests : null,
          additional_notes: formData.additionalNotes || null,
          contact_first_name: formData.contactInfo.firstName,
          contact_last_name: formData.contactInfo.lastName,
          contact_email: formData.contactInfo.email,
          contact_phone: formData.contactInfo.phone,
          travel_designer_preference: formData.autoAssignDesigner ? null : (formData.travelDesigner || null),
          assigned_designer_id: null, // Esplicito: nessun designer assegnato alla creazione
          status: 'pending'
        });

      if (error) {
        toast({
          title: "Errore nell'invio",
          description: error.message,
          variant: "destructive",
        });
        return;
      } else {
        toast({
          title: "Richiesta inviata! 🎉",
          description: "Ti metteremo in contatto con il travel designer perfetto entro 24 ore.",
        });
      }


      // Show confirmation
      setShowConfirmation(true);

      // Reset form
      setFormData({
        destinations: [],
        departurePoint: "",
        needsTransportation: false,
        autoAssignDesigner: true,
        travelDesigner: null,
        totalGuests: 1,
        guests: [{ id: 1, age: 25 }],
        travelDates: {
          startDate: "",
          endDate: "",
          flexible: true
        },
        budget: {
          min: "",
          max: "",
          flexible: true
        },
        interests: [],
        additionalNotes: "",
        contactInfo: {
          firstName: "",
          lastName: "",
          email: "",
          phone: ""
        }
      });
    } catch (error) {
      console.error('Unexpected error:', error);
      toast({
        title: "Errore imprevisto",
        description: "Si è verificato un errore imprevisto. Riprova.",
        variant: "destructive",
      });
      // showDevError({ error, context: 'unexpected error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show confirmation if form was submitted
  if (showConfirmation) {
    return (
      <div className="min-h-screen bg-white text-gray-900 h-full flex items-center justify-center">
        <Card className="border border-gray-200 rounded-2xl w-full max-w-lg">
          <CardContent className="text-center p-8">
            <div className="flex flex-col items-center space-y-8">
              {/* Icon */}
              <div className="w-14 h-14 bg-gray-900 rounded-full flex items-center justify-center">
                <Mail className="w-7 h-7 text-white" />
              </div>

              {/* Title */}
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  Thank you for reaching out!
                </h1>
                <p className="text-sm text-gray-600">
                  We'll be in touch within one business day
                </p>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center space-y-5 mt-10">
              {/* Primary Button */}
              <Button
                className="bg-gray-900 hover:bg-gray-800 text-white rounded-full"
                onClick={() => navigate('/dashboard')}
              >
                Back to Place2Be
              </Button>

              {/* Secondary Button */}
              <Button
                variant="outline"
                className="bg-gray-50 hover:bg-gray-100 text-gray-900 border-gray-200 rounded-full"
                onClick={() => setShowConfirmation(false)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Plan another trip
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col items-center my-8">
          <div className="flex items-center mb-4">
            <img
              src="/assets/images/dark_small.svg"
              alt="Place2Be Logo"
              className="w-12 h-12 mr-3"
            />
            <h1 className="text-3xl font-bold">
              Plan your stay
            </h1>
          </div>
          <p className="text-lg">
            Fill out the form to receive your personalized proposal
          </p>

          {/* Bottone per dati di esempio (solo in development) */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={applySampleData}
                className="text-xs bg-yellow-50 border-yellow-200 text-yellow-700 hover:bg-yellow-100"
              >
                🧪 Completa Form (Dev)
              </Button>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Destinations Section */}
          <Card className="border border-gray-200 rounded-2xl">
            <CardContent className="p-6 space-y-8">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2">
                    Where do you want to go? <span className="text-red-500">*</span>
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Add one or more destinations
                  </p>
                </div>

                {formData.destinations.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.destinations.map((destination, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="px-4 py-1 rounded-full border bg-white border-gray-200 hover:bg-gray-50 cursor-pointer"
                        onClick={() => removeDestination(destination)}
                      >
                        {destination}
                        <span className="ml-2 text-lg text-gray-500">×</span>
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="flex gap-4">
                  <div className="w-full lg:w-1/2 relative">
                    <Globe className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      ref={destinationInputRef}
                      placeholder="Asia, France, Tuscany, New York..."
                      className="pl-12 pr-4 py-3 rounded-xl"
                      hasError={validationErrors.has('destinations')}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter') return;
                        e.preventDefault();
                        handleAddDestination();
                      }}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="px-6 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100"
                    onClick={handleAddDestination}
                  >
                    Add destination
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Travel Designer Section */}
          <Card className="border border-gray-200 rounded-2xl">
            <CardContent className="p-6 space-y-8">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">
                  Who will be your Travel Designer? <span className="text-red-500">*</span>
                </h3>

                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="auto-assign"
                      checked={formData.autoAssignDesigner}
                      onCheckedChange={(checked) => {
                        updateFormData({ 
                          autoAssignDesigner: checked as boolean,
                          // Se attivi auto-assign, pulisci la selezione manuale
                          travelDesigner: checked ? null : formData.travelDesigner
                        });
                      }}
                    />
                    <Label htmlFor="auto-assign">
                      I let Place2Be choose for me
                    </Label>
                  </div>

                  <div className={`space-y-2 lg:w-1/2 w-full`}>
                    <Label className="text-sm text-gray-600">
                      Or choose a specific designer {travelDesigners.length > 0 && `(${travelDesigners.length} available)`}
                    </Label>
                    <AutocompleteSelect
                      options={travelDesigners.map((designer) => ({
                        value: designer.id,
                        label: designer.name
                      }))}
                      value={formData.travelDesigner}
                      onValueChange={(value) => {
                        updateFormData({ travelDesigner: value || null });
                      }}
                      placeholder={travelDesigners.length === 0 ? "No designers available" : "Search your designer"}
                      searchPlaceholder="Search designers by name or specialty..."
                      emptyMessage="No designers found matching your search."
                      disabled={formData.autoAssignDesigner || travelDesigners.length === 0}
                      className="w-full lg:w-3/4"
                      triggerClassName="border rounded-xl"
                      hasError={validationErrors.has('travelDesigner')}
                      displayValue={(option) => {
                        if (!option) return "Search your designer";
                        return (
                          <div className="flex flex-col items-start">
                            <span className="font-medium">{option.label}</span>
                          </div>
                        );
                      }}
                      searchValue={(option) => `${option.label}`}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Trip Details Section */}
          <div className="space-y-6">
            <h3 className="text-md text-center">
              Now, let's dive into the details of the trip!
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Departure Point Section */}
              <Card className="border border-gray-200 rounded-2xl">
                <CardContent className="p-6 space-y-8">
                  <div className="space-y-8">
                    <h3 className="text-lg font-semibold">
                      Where are you departing from? <span className="text-red-500">*</span>
                    </h3>

                    <div className="space-y-8">
                      <IconInput
                        icon={MapPin}
                        placeholder="Enter your departure city"
                        value={formData.departurePoint}
                        onChange={(value) => updateFormData({ departurePoint: value })}
                        hasError={validationErrors.has('departurePoint')}
                      />

                      <div className="flex items-center space-x-3">
                        <Checkbox
                          id="transportation"
                          checked={formData.needsTransportation}
                          onCheckedChange={(checked) => updateFormData({ needsTransportation: checked as boolean })}
                        />
                        <Label htmlFor="transportation" className="font-normal">
                          I need Place2Be to arrange transportation from my departure point to my destination.
                        </Label>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* When Section */}
              <Card className="border border-gray-200 rounded-2xl">
                <CardContent className="p-6 space-y-8">
                  <h4 className="text-lg font-semibold">When? <span className="text-red-500">*</span></h4>

                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={!formData.travelDates.flexible ? "default" : "outline"}
                        className={`px-6 py-2 rounded-full ${!formData.travelDates.flexible ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200'}`}
                        onClick={() => updateNestedField('travelDates', { flexible: false })}
                      >
                        Exact dates
                      </Button>
                      <Button
                        type="button"
                        variant={formData.travelDates.flexible ? "default" : "outline"}
                        className={`px-6 py-2 rounded-full ${formData.travelDates.flexible ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200'}`}
                        onClick={() => updateNestedField('travelDates', { flexible: true })}
                      >
                        Flexible
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <DatePicker
                        value={formData.travelDates.startDate}
                        onSelect={(date) => updateNestedField('travelDates', { startDate: date })}
                        placeholder="Start date"
                        hasError={validationErrors.has('travelStartDate')}
                        minDate={new Date()}
                      />
                      <DatePicker
                        value={formData.travelDates.endDate}
                        onSelect={(date) => updateNestedField('travelDates', { endDate: date })}
                        placeholder="End date"
                        hasError={validationErrors.has('travelEndDate')}
                        minDate={formData.travelDates.startDate ? new Date(formData.travelDates.startDate) : new Date()}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Budget Section */}
              <Card className="border border-gray-200 rounded-2xl">
                <CardContent className="p-6 space-y-8">
                  <h4 className="text-lg font-semibold">What's your budget per person? <span className="text-red-500">*</span></h4>

                  <div className="space-y-8">
                    <div className="grid grid-cols-2 gap-4">
                      <IconInput
                        icon={Euro}
                        placeholder="Min"
                        value={formData.budget.min}
                        onChange={(value) => updateNestedField('budget', { min: value })}
                        type="number"
                        hasError={validationErrors.has('budgetMin')}
                      />
                      <IconInput
                        icon={Euro}
                        placeholder="Max"
                        value={formData.budget.max}
                        onChange={(value) => updateNestedField('budget', { max: value })}
                        type="number"
                        hasError={validationErrors.has('budgetMax')}
                      />
                    </div>

                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="flexible-budget"
                        checked={formData.budget.flexible}
                        onCheckedChange={(checked) => updateNestedField('budget', { flexible: checked as boolean })}
                      />
                      <Label htmlFor="flexible-budget" className="font-normal">
                        I can increase my budget
                      </Label>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Additional Notes Section */}
              <Card className="border border-gray-200 rounded-2xl">
                <CardContent className="p-6 space-y-8">
                  <h4 className="text-lg font-semibold text-gray-900">Do you have any additional notes?</h4>

                  <div className="relative">
                    <MessageSquare className="absolute left-4 top-4 text-gray-400 w-5 h-5" />
                    <Textarea
                      placeholder="Tell us about any special interests, dietary requirements, accessibility needs, or anything else we should know..."
                      className="pl-12 pr-4 py-4 border border-gray-200 rounded-xl resize-none min-h-[130px]"
                      value={formData.additionalNotes}
                      onChange={(e) => updateFormData({ additionalNotes: e.target.value })}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Who Section */}
            <Card className="border border-gray-200 rounded-2xl">
              <CardContent className="p-6 space-y-8">
                <h4 className="text-lg font-semibold">Who? <span className="text-red-500">*</span></h4>
                <GuestManager
                  totalGuests={formData.totalGuests}
                  guests={formData.guests}
                  onAddGuest={addGuest}
                  onRemoveGuest={removeGuest}
                  onUpdateGuestAge={updateGuestAge}
                />
              </CardContent>
            </Card>

            {/* Interests Section */}
            <Card className="border border-gray-200 rounded-2xl">
              <CardContent className="p-6 space-y-8">
                <h4 className="text-lg font-semibold text-gray-900">What do you enjoy when traveling?</h4>

                <div className="flex flex-wrap gap-3">
                  {interestOptions.map((interest) => {
                    const Icon = interest.icon;
                    const isSelected = formData.interests.includes(interest.id);

                    return (
                      <Button
                        key={interest.id}
                        type="button"
                        variant={isSelected ? "default" : "outline"}
                        className={`px-4 py-2 rounded-full border ${isSelected
                          ? 'bg-gray-900 text-white border-gray-900'
                          : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                          }`}
                        onClick={() => toggleInterest(interest.id)}
                      >
                        <Icon className="w-4 h-4 mr-2" />
                        {interest.label}
                      </Button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* User Details Section */}
          <div className="space-y-6">
            <h3 className="text-md text-center">
              Tell me something about yourself and we're ready!
            </h3>

            <Card className="border border-gray-200 rounded-2xl">
              <CardContent className="p-6 space-y-8">
                <h4 className="text-lg font-semibold">
                  How can we reach you? <span className="text-red-500">*</span>
                </h4>

                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <IconInput
                      icon={Users}
                      placeholder="First name"
                      value={formData.contactInfo.firstName}
                      onChange={(value) => updateNestedField('contactInfo', { firstName: value })}
                      hasError={validationErrors.has('contactFirstName')}
                    />
                    <IconInput
                      icon={Users}
                      placeholder="Last name"
                      value={formData.contactInfo.lastName}
                      onChange={(value) => updateNestedField('contactInfo', { lastName: value })}
                      hasError={validationErrors.has('contactLastName')}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <IconInput
                      icon={MessageSquare}
                      placeholder="Email address"
                      value={formData.contactInfo.email}
                      onChange={(value) => updateNestedField('contactInfo', { email: value })}
                      type="email"
                      hasError={validationErrors.has('contactEmail')}
                    />
                    <IconInput
                      icon={MessageSquare}
                      placeholder="Phone number"
                      value={formData.contactInfo.phone}
                      onChange={(value) => updateNestedField('contactInfo', { phone: value })}
                      type="tel"
                      hasError={validationErrors.has('contactPhone')}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Submit Button */}
          <div className="text-center pt-8">
            <Button
              type="submit"
              size="lg"
              className="bg-gray-900 hover:bg-gray-800 text-white text-sm px-12 py-4 rounded-xl"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader size="md" className="mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 w-5 h-5" />
                  Submit trip request
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestTrip;
